import { Button, Grid, Stack } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'

import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import { getIsDynatraceIntelligentHealthCheckRequest } from 'components/status-checks/create/create-status-check-utils'
import { SaveStatusCheckFormButton } from 'components/status-checks/form/status-check/SaveStatusCheckFormButton'
import { useStatusCheckRequestBuilder } from 'components/status-checks/hooks/use-status-check-request-builder'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import toast from 'lib/toast'
import { useCreateDynatraceStatusCheck } from 'queries/status-checks/use-create-dynatrace-status-check'
import { useCreateStatusCheck } from 'queries/status-checks/use-create-status-check'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import { useUpdateDynatraceStatusCheck } from 'queries/status-checks/use-update-dynatrace-status-check'
import { useUpdateStatusCheck } from 'queries/status-checks/use-update-status-check'
import { type ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'
import { handleDataFetchOnChange } from 'utils/handle-data-fetch-on-change'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

/**
 * These fields don't require re-testing by the user for the form to be considered valid.
 * Manual re-validation (via the user testing the connection and evaluation sections)
 * is required for any fields in the monitor/alert and success criteria sections.
 */
const FIELDS_THAT_DONT_REQUIRE_RETEST: (keyof StatusCheckFormValues)[] = [
  'category',
  'pollingIntervalSeconds',
  'statusCheckName'
]

export const EditStatusCheckForm: React.FC<{
  headerContent?: React.ReactNode
  statusCheck: ServiceStatusCheckFromStatusCheck
}> = ({ headerContent, statusCheck }) => {
  const isClone = statusCheck.id === null
  const location = useLocation()
  const navigate = useNavigate()
  const statusChecksUrls = getStatusChecksUrls()
  const { formState, handleSubmit } = useFormContext<StatusCheckFormValues>()
  const { isSubmitting } = formState
  const { referrer = statusChecksUrls.base() } = location.state ?? {}
  const { refetch: reFetchStatusChecks } = useGetStatusChecks({
    enabled: false
  })
  const { mutateAsync: createStatusCheck } = useCreateStatusCheck()
  const { mutateAsync: updateStatusCheck } = useUpdateStatusCheck(
    statusCheck.id
  )
  const { mutateAsync: createDynatraceStatusCheck } =
    useCreateDynatraceStatusCheck()
  const { mutateAsync: updateDynatraceStatusCheck } =
    useUpdateDynatraceStatusCheck(statusCheck.id)

  const buildSaveStatusCheckRequest = useStatusCheckRequestBuilder()
  const handleSaveStatusCheck = async (values: StatusCheckFormValues) => {
    try {
      const request = await buildSaveStatusCheckRequest(values)
      const isDynatraceIntelligentHealthCheckRequest =
        getIsDynatraceIntelligentHealthCheckRequest(request)

      if (isClone) {
        isDynatraceIntelligentHealthCheckRequest
          ? await createDynatraceStatusCheck(request)
          : await createStatusCheck(request)
      } else {
        isDynatraceIntelligentHealthCheckRequest
          ? await updateDynatraceStatusCheck(request)
          : await updateStatusCheck(request)
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: ms.seconds(2)
      })

      // Refresh status checks list
      await handleDataFetchOnChange(reFetchStatusChecks)

      // Navigate to back to status check list
      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  return (
    <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
      <Grid container>
        <Grid item lg={9} xs={12}>
          <Stack sx={{ rowGap: 3 }}>
            {headerContent}
            <StatusCheckConnectionForm />
            <Stack direction="row" sx={{ columnGap: 2 }}>
              <SaveStatusCheckFormButton
                data-testid="edit-status-check-save-button"
                dirtyFieldOverrides={FIELDS_THAT_DONT_REQUIRE_RETEST}
                label={isSubmitting ? ' Saving...' : 'Save Health Check'}
              />
              <Button
                color="secondary"
                component={StyledRouterLink}
                disabled={isSubmitting}
                to={referrer === '/' ? statusChecksUrls.list() : referrer}
                variant="contained"
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
